/**
 * URL / cookie params for the PostAJob flow (Fillout parity).
 */

export const ANSWER_QUERY_KEYS = {
  role: 'role',
  skill: 'skills',
  email: 'email',
  description: 'description',
  company: 'companyName',
};

export const PASSTHROUGH_PARAM_KEYS = [
  'freelancer',
  'username',
  'url',
  'source',
  'medium',
  'campaign',
  'content',
  'term',
  'gclid',
  'fbclid',
  'msclkid',
  'li_fat_id',
];

export const SUPPORTED_QUERY_PARAMS = [
  ...Object.keys(ANSWER_QUERY_KEYS),
  ...PASSTHROUGH_PARAM_KEYS,
];

const ATTRIBUTION_COOKIE_MAP = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  utm_term: 'term',
};

const CLICK_ID_PARAMS = ['gclid', 'msclkid', 'fbclid', 'li_fat_id'];

const CLICK_ID_SOURCE_FALLBACKS = {
  gclid: { source: 'google', medium: 'cpc' },
  msclkid: { source: 'bing', medium: 'cpc' },
  fbclid: { source: 'meta', medium: 'paid-social' },
  li_fat_id: { source: 'linkedin.com', medium: 'paid-social' },
};

const readSearchParams = (search) => {
  const query = typeof search === 'string' ? search : '';
  const normalized = query.charAt(0) === '?' ? query.slice(1) : query;
  return new URLSearchParams(normalized);
};

const getParamValue = (searchParams, key) => {
  const value = searchParams.get(key);
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * @param {string} search - location.search (with or without ?)
 * @param {(name: string) => string|null|undefined} [getCookie]
 * @returns {{ answersPatch: Object, params: Object }}
 */
export const readFlowQueryParams = (search, getCookie = () => null) => {
  const searchParams = readSearchParams(search);
  const params = {};

  PASSTHROUGH_PARAM_KEYS.forEach((key) => {
    const value = getParamValue(searchParams, key);
    if (value) {
      params[key] = value;
    }
  });

  // Click IDs from cookies when missing from URL
  CLICK_ID_PARAMS.forEach((key) => {
    if (params[key]) {
      return;
    }
    const cookieValue = getCookie(key);
    if (cookieValue) {
      params[key] = String(cookieValue);
    }
  });

  // Attribution: URL wins, cookies fill gaps
  Object.keys(ATTRIBUTION_COOKIE_MAP).forEach((cookieName) => {
    const paramKey = ATTRIBUTION_COOKIE_MAP[cookieName];
    if (params[paramKey]) {
      return;
    }
    const cookieValue = getCookie(cookieName);
    if (cookieValue) {
      params[paramKey] = String(cookieValue);
    }
  });

  // Infer source/medium from click ID when still unset
  if (!params.source) {
    for (let i = 0; i < CLICK_ID_PARAMS.length; i += 1) {
      const clickId = CLICK_ID_PARAMS[i];
      if (params[clickId] && CLICK_ID_SOURCE_FALLBACKS[clickId]) {
        const fallback = CLICK_ID_SOURCE_FALLBACKS[clickId];
        params.source = fallback.source;
        if (fallback.medium && !params.medium) {
          params.medium = fallback.medium;
        }
        break;
      }
    }
  }

  const answersPatch = {};

  Object.keys(ANSWER_QUERY_KEYS).forEach((queryKey) => {
    const answerKey = ANSWER_QUERY_KEYS[queryKey];
    const value = getParamValue(searchParams, queryKey);
    if (value) {
      answersPatch[answerKey] = value;
    }
  });

  if (answersPatch.description) {
    answersPatch.hasDescription = 'yes';
  }

  return { answersPatch, params };
};

/**
 * Merge query hydration into existing answers without overwriting user input.
 */
export const mergeAnswerPatch = (existingAnswers, answersPatch) => {
  const next = { ...existingAnswers };
  Object.keys(answersPatch || {}).forEach((key) => {
    const incoming = answersPatch[key];
    if (incoming == null || incoming === '') {
      return;
    }
    const current = next[key];
    const isEmpty =
      current == null ||
      current === '' ||
      (Array.isArray(current) && current.length === 0);
    if (isEmpty) {
      next[key] = incoming;
    }
  });
  return next;
};

export const mergeParams = (existingParams, incomingParams) => ({
  ...(existingParams && typeof existingParams === 'object'
    ? existingParams
    : {}),
  ...(incomingParams && typeof incomingParams === 'object'
    ? incomingParams
    : {}),
});

/**
 * Build navigation search from attribution params + the original landing query.
 * Replays landing answer keys (role, skill, email, …) for Fillout parity on
 * Start again — never copies live typed answers from the snapshot.
 *
 * @param {Object} [params] - passthrough / attribution from the snapshot
 * @param {string} [landingSearch] - location.search captured on first mount
 * @returns {string} search including leading "?" or ""
 */
export const buildFlowSearch = (params = {}, landingSearch = '') => {
  const searchParams = new URLSearchParams();
  const landingParams = readSearchParams(landingSearch);

  Object.keys(ANSWER_QUERY_KEYS).forEach((key) => {
    const value = getParamValue(landingParams, key);
    if (value) {
      searchParams.set(key, value);
    }
  });

  PASSTHROUGH_PARAM_KEYS.forEach((key) => {
    const value = params[key];
    if (typeof value === 'string' && value.trim()) {
      searchParams.set(key, value.trim());
    }
  });

  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : '';
};
